/**
 * Het advertentielogboek van één woning (MA-22): wanneer stond hij te koop,
 * wanneer was hij weg, wanneer kwam hij terug, en voor welke prijs.
 *
 * Er komt geen nieuwe tabel voor. Alles staat al in `listing_mutatie` (MA-3):
 * elke volledige ronde schrijft `status: actief -> verdwenen` voor wat ontbrak
 * en `verdwenen -> actief` voor wat terugkwam, en elke prijswijziging staat er
 * als `vraagprijs`. Het logboek is die rijen, op volgorde gelegd, met de prijs
 * van dat moment erbij.
 *
 * De prijs "op elk moment" is terug te rekenen: de eerste prijsmutatie draagt in
 * `oudeWaarde` de prijs waarmee hij binnenkwam. Zonder prijsmutaties is de
 * huidige vraagprijs ook de eerste.
 *
 * Puur en zonder database, zodat het te testen is en de API en het scherm het
 * over dezelfde volgorde eens zijn.
 */
import { z } from 'zod';
import { aanbodSchema, aanbodVan } from './aanbod';
import { isoDatumTijdSchema } from './common';

export const LOGBOEKSOORTEN = [
  'eerste_waarneming',
  'prijs',
  'merkje',
  'verdwenen',
  'teruggekomen',
] as const;
export type Logboeksoort = (typeof LOGBOEKSOORTEN)[number];

export const logboekGebeurtenisSchema = z.object({
  op: isoDatumTijdSchema,
  soort: z.enum(LOGBOEKSOORTEN),
  /** De vraagprijs ná deze gebeurtenis; null als die niet terug te rekenen is. */
  vraagprijs: z.number().int().nullable(),
  /** Alleen bij `prijs`: wat het was. */
  vorigePrijs: z.number().int().nullable(),
  /** Alleen bij `merkje`: het nieuwe merkje, of null als het eraf ging. */
  merkje: z.string().nullable(),
});
export type LogboekGebeurtenis = z.infer<typeof logboekGebeurtenisSchema>;

export const logboekPeriodeSchema = z.object({
  van: isoDatumTijdSchema,
  /** Null zolang hij in deze periode nog te koop staat. */
  tot: isoDatumTijdSchema.nullable(),
  prijsBegin: z.number().int().nullable(),
  prijsEind: z.number().int().nullable(),
});
export type LogboekPeriode = z.infer<typeof logboekPeriodeSchema>;

export const logboekSchema = z.object({
  listingId: z.uuid(),
  aanbod: aanbodSchema,
  /** De eerste keer dat wij hem zagen. */
  eersteWaarnemingOp: isoDatumTijdSchema,
  /** Hoe vaak hij na een verdwijning terugkwam. */
  terugkomsten: z.number().int().nonnegative(),
  laatstTerugOp: isoDatumTijdSchema.nullable(),
  /** Oudste eerst: zo lees je een geschiedenis. */
  gebeurtenissen: z.array(logboekGebeurtenisSchema),
  /** De aaneengesloten stukken waarin hij te koop stond, oudste eerst. */
  perioden: z.array(logboekPeriodeSchema),
});
export type Logboek = z.infer<typeof logboekSchema>;

/** Wat het logboek van de woning zelf nodig heeft. */
export type LogboekWoning = {
  id: string;
  aangemaaktOp: Date | string;
  vraagprijs: number;
  status: 'actief' | 'verdwenen';
  badge: string | null;
};

/** Eén mutatierij zoals de database hem levert. */
export type LogboekMutatie = {
  waargenomenOp: Date | string;
  veld: string;
  oudeWaarde: string | null;
  nieuweWaarde: string | null;
};

/**
 * Merkjes die iets zeggen over de verkoop. "Nieuw" en "Open huis" komen en gaan
 * elke week; die in het logboek zetten maakt het onleesbaar.
 */
export function isVerkoopmerkje(merkje: string | null | undefined): boolean {
  const klein = (merkje ?? '').toLowerCase();
  return /verkocht|voorbehoud|optie|bod|under_|sold/.test(klein);
}

function iso(waarde: Date | string): string {
  return waarde instanceof Date ? waarde.toISOString() : new Date(waarde).toISOString();
}

function alsPrijs(waarde: string | null): number | null {
  if (waarde === null || !/^\d+$/.test(waarde.trim())) return null;
  return Number(waarde.trim());
}

/**
 * Bij gelijke tijd eerst de prijs, dan de status: een woning die terugkomt met
 * een nieuwe prijs krijgt die twee mutaties in dezelfde haal, en "terug voor
 * € 540.000" hoort de nieuwe prijs te noemen.
 */
function volgorde(veld: string): number {
  if (veld === 'vraagprijs') return 0;
  if (veld === 'status') return 2;
  return 1;
}

export function bouwLogboek(woning: LogboekWoning, mutaties: readonly LogboekMutatie[]): Logboek {
  const gesorteerd = [...mutaties].sort((a, b) => {
    const tijd = new Date(a.waargenomenOp).getTime() - new Date(b.waargenomenOp).getTime();
    return tijd !== 0 ? tijd : volgorde(a.veld) - volgorde(b.veld);
  });

  const eerstePrijsmutatie = gesorteerd.find((m) => m.veld === 'vraagprijs');
  let prijs: number | null = eerstePrijsmutatie
    ? alsPrijs(eerstePrijsmutatie.oudeWaarde)
    : woning.vraagprijs;

  const eersteOp = iso(woning.aangemaaktOp);
  const gebeurtenissen: LogboekGebeurtenis[] = [
    {
      op: eersteOp,
      soort: 'eerste_waarneming',
      vraagprijs: prijs,
      vorigePrijs: null,
      merkje: null,
    },
  ];
  const perioden: LogboekPeriode[] = [];
  let open: LogboekPeriode | null = {
    van: eersteOp,
    tot: null,
    prijsBegin: prijs,
    prijsEind: prijs,
  };
  let terugkomsten = 0;
  let laatstTerugOp: string | null = null;

  for (const mutatie of gesorteerd) {
    const op = iso(mutatie.waargenomenOp);

    if (mutatie.veld === 'vraagprijs') {
      const vorige = prijs;
      prijs = alsPrijs(mutatie.nieuweWaarde);
      if (open) open.prijsEind = prijs;
      gebeurtenissen.push({
        op,
        soort: 'prijs',
        vraagprijs: prijs,
        vorigePrijs: vorige,
        merkje: null,
      });
      continue;
    }

    if (mutatie.veld === 'badge') {
      if (!isVerkoopmerkje(mutatie.nieuweWaarde) && !isVerkoopmerkje(mutatie.oudeWaarde)) continue;
      gebeurtenissen.push({
        op,
        soort: 'merkje',
        vraagprijs: prijs,
        vorigePrijs: null,
        merkje: mutatie.nieuweWaarde,
      });
      continue;
    }

    if (mutatie.veld !== 'status') continue;

    if (mutatie.nieuweWaarde === 'verdwenen') {
      if (open) {
        open.tot = op;
        perioden.push(open);
        open = null;
      }
      gebeurtenissen.push({
        op,
        soort: 'verdwenen',
        vraagprijs: prijs,
        vorigePrijs: null,
        merkje: null,
      });
    } else if (mutatie.nieuweWaarde === 'actief' && mutatie.oudeWaarde === 'verdwenen') {
      terugkomsten += 1;
      laatstTerugOp = op;
      if (open) perioden.push(open);
      open = { van: op, tot: null, prijsBegin: prijs, prijsEind: prijs };
      gebeurtenissen.push({
        op,
        soort: 'teruggekomen',
        vraagprijs: prijs,
        vorigePrijs: null,
        merkje: null,
      });
    }
  }

  if (open) perioden.push(open);

  return {
    listingId: woning.id,
    aanbod: aanbodVan(woning.status, woning.badge),
    eersteWaarnemingOp: eersteOp,
    terugkomsten,
    laatstTerugOp,
    gebeurtenissen,
    perioden,
  };
}

/** Of deze woning al eens uit het aanbod was en terugkwam. */
export function isEerderActief(woning: { terugkomsten: number }): boolean {
  return woning.terugkomsten > 0;
}
